// Compact "Units at this location" panel. Picker sees other SKUs they
// still need to grab from the bay they're standing at, so they can
// scoop them in the same visit rather than walking back. The head's
// right side carries the location-level unit counter — total units to
// pick here across ALL SKUs in the run vs how many are picked, moving
// live on every accepted pick at the location (sibling SKUs included).
//
// `max` is settings.picking_other_skus_count (0-10). Anything beyond is
// folded into a "+N more" orange pill so the panel never grows tall.
import { useTranslation } from "../../i18n";
import { groupBarcode } from "./barcodeFormat";
import "./PickingScreen.css";

export interface AlsoItem {
    sku: string;
    title: string | null;
    thumbnail_url?: string | null;
    /** Primary product barcode — what the picker matches against the
     *  physical label. Falls back to SKU in the row when absent. */
    barcode?: string | null;
    qty_required: number;
}

type Props = {
    items: AlsoItem[];
    /** Max sibling rows to render (settings.picking_other_skus_count).
     *  0 hides the ROWS only — the unit counter still shows when the
     *  location holds more than one SKU. */
    max: number;
    /** Location-level unit counter (picked/total across all SKUs at
     *  the location). Null/undefined — older BE payload — hides the
     *  counter; the panel otherwise renders as before. */
    locationQtyTotal?: number | null;
    locationQtyPicked?: number | null;
    /** Distinct SKUs the run has at this location. The counter only
     *  shows when > 1 — a single-SKU location's unit count is already
     *  the hero's own counter, so repeating it here is noise. */
    locationSkuCount?: number | null;
};

const AlsoAtLocation = ({ items, max, locationQtyTotal, locationQtyPicked, locationSkuCount }: Props) => {
    const { t } = useTranslation();
    // Counter: needs the location-level data AND more than one SKU at
    // the location. Deliberately run-scoped (not remaining-scoped) —
    // it stays up after the last sibling SKU is fully picked, until
    // the location completes and the pick moves on.
    const showCounter = locationQtyTotal != null && (locationSkuCount ?? 0) > 1;
    // Sibling rows: gated on the warehouse setting. With the setting
    // at 0 neither the rows nor the "+N more" pill render — the panel
    // is just the head with the counter.
    const shown = max > 0 ? items.slice(0, max) : [];
    const remaining = max > 0 ? items.length - shown.length : 0;
    if (!showCounter && shown.length === 0) return null;
    return (
        <div className="ps-more">
            <div className="ps-more-head">
                <div className="ps-more-head-left">
                    <span className="ps-more-eyebrow">{t("picking_units_at_location")}</span>
                    {remaining > 0 && (
                        <span className="ps-more-count">{t("picking_also_more", { n: String(remaining) })}</span>
                    )}
                </div>
                {/* Same treatment as the SKU counter in the product row
                    (.ps-qty), two size steps down — but LOCATION-scoped:
                    both numbers come from the run's lines at this
                    location, not the active line item. */}
                {showCounter && (
                    <span className="ps-qty ps-qty--sm" aria-label={t("picking_units_at_location")}>
                        <span className="ps-qty-picked">{locationQtyPicked ?? 0}</span>
                        <span className="ps-qty-slash">/</span>
                        <span className="ps-qty-total">{locationQtyTotal}</span>
                    </span>
                )}
            </div>
            {shown.length > 0 && (
                <ul className="ps-more-list">
                    {shown.map(it => (
                        <li key={it.sku} className="ps-more-row">
                            <div className="ps-more-col">
                                {it.title && <div className="ps-more-title">{it.title}</div>}
                                {/* Barcode, not SKU — matches the physical
                                    label, same treatment as the hero. */}
                                <div className="ps-more-sku">{groupBarcode(it.barcode || it.sku)}</div>
                            </div>
                            <div className="ps-more-qty">×{it.qty_required}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AlsoAtLocation;
